import { defineStore } from 'pinia';
import { ref, shallowRef, watch } from 'vue';
import type {
  ActivePopupItemState,
  ChartContextData,
  OpenPopupOptions,
  ContextInfo,
} from '@/dashboard/features/chartPopup/types/chartContext.types';
import { createDefaultChartContextFromPopupItem } from '../features/chartPopup/utils/chartContext.utils';
import { toComposedId } from '../features/chartPopup/domain/chartPopupItem';
import type {
  ChartPopupItemEntry,
  ComposedId,
  ChartPopupItem,
} from '../features/chartPopup/domain/chartPopupItem';

const comparePopupItemByValue = (a: ChartPopupItem, b: ChartPopupItem) => {
  if (a.value === b.value) return 0;
  if (a.value == null) return 1;
  if (b.value == null) return -1;
  return b.value - a.value;
};

export const useChartContextStore = defineStore('chartContextStore', () => {
  const sourceWidgetId = ref<string | undefined>(undefined);
  const isPopupOpen = ref(false);
  const popupItemEntries = shallowRef<ChartPopupItemEntry[]>([]);
  const activeComposedId = ref<ComposedId | undefined>(undefined);
  const activePopupItemState = shallowRef<ActivePopupItemState | undefined>(undefined);
  // 더블클릭마다 ChartPopup 위치를 마우스 좌표로 재배치하기 위한 트리거 시퀀스
  const popupOpenSeq = ref(0);

  let currentLabel: number | null = null;

  let getContextInfoList: (
    popupItem?: ChartPopupItem,
    label?: number | null,
  ) => ContextInfo[] = () => [];
  let createChartContext: (
    popupItem: ChartPopupItem | undefined,
    label: number | null,
  ) => ChartContextData | undefined = createDefaultChartContextFromPopupItem;

  let popupItemLookup = new Map<ComposedId, ChartPopupItem>();
  const buildPopupItems = (groups: Record<string, ChartPopupItem[]>) => {
    popupItemLookup = new Map();
    const allEntries: ChartPopupItemEntry[] = [];

    for (const [chartDataId, items] of Object.entries(groups)) {
      for (const item of items) {
        const composedId = toComposedId(chartDataId, item.id);
        popupItemLookup.set(composedId, item);
        allEntries.push({ composedId, popupItem: item });
      }
    }

    allEntries.sort((a, b) => comparePopupItemByValue(a.popupItem, b.popupItem));
    popupItemEntries.value = allEntries;
  };

  const findPopupItem = (composedId: ComposedId): ChartPopupItem | undefined => {
    return popupItemLookup.get(composedId);
  };

  const openDefaultPopup = (options: OpenPopupOptions) => {
    sourceWidgetId.value = options.widgetId;
    currentLabel = options.label ?? null;
    getContextInfoList = options.getContextInfoList;
    createChartContext = options.createChartContext ?? createDefaultChartContextFromPopupItem;

    // stale composedId 방지: 항상 초기화 후 재설정
    activeComposedId.value = undefined;

    if (options.popupItemsByChartData) {
      buildPopupItems(options.popupItemsByChartData);
      if (popupItemEntries.value.length === 0) return;
      activeComposedId.value = toComposedId(options.chartDataId, options.popupItemId);
    } else {
      popupItemEntries.value = [];
      popupItemLookup = new Map();
    }

    isPopupOpen.value = true;
    popupOpenSeq.value += 1;
  };

  const closeChartPopup = () => {
    isPopupOpen.value = false;
    activeComposedId.value = undefined;
    sourceWidgetId.value = undefined;
    popupItemEntries.value = [];
    popupItemLookup = new Map();
    getContextInfoList = () => [];
    createChartContext = createDefaultChartContextFromPopupItem;
  };

  // flush: 'sync' — WideDropdownSelect v-model 변경이 즉시 반영되어야 함
  watch(
    [isPopupOpen, activeComposedId],
    ([open, composedId]) => {
      if (!open) {
        activePopupItemState.value = undefined;
        return;
      }
      if (!composedId) {
        // links-only: item 없이 열린 상태 (TreeGrid 등)
        activePopupItemState.value = {
          popupItem: undefined,
          chartContext: undefined,
          contextInfoList: getContextInfoList(),
        };
        return;
      }
      const popupItem = findPopupItem(composedId);
      if (popupItem) {
        const label = popupItem.label ?? currentLabel;
        activePopupItemState.value = {
          popupItem,
          chartContext: createChartContext(popupItem, label),
          contextInfoList: getContextInfoList(popupItem, label),
        };
        return;
      }
      activePopupItemState.value = undefined;
    },
    { flush: 'sync' },
  );

  return {
    isPopupOpen,
    activeComposedId,
    sourceWidgetId,
    activePopupItemState,
    popupItemEntries,
    popupOpenSeq,
    openDefaultPopup,
    closeChartPopup,
  };
});
